import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";

/**
 * Sanitizes a string value by trimming whitespace
 * Returns undefined if the value is not a string or is empty after trimming
 *
 * @param value - The string value to sanitize
 * @returns The trimmed string or undefined
 */
function sanitizeString(value?: string | null) {
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();

  return trimmed.length ? trimmed : undefined;
}

/**
 * Calculate days left until expiration
 * @param expirationDate - Expiration date string
 * @returns Number of days remaining
 */
function calculateDaysLeft(expirationDate: string) {
  const today = dayjs();
  return dayjs(expirationDate).diff(today, "day");
}

/**
 * Tipo de objeto padrão para resultados de endpoints.
 * Representa um registro onde os valores podem ser string, number ou undefined.
 */
type Result = Record<string, string | number | undefined>;

/**
 * Tipo auxiliar para extrair uma chave de resultado.
 * Se TResult for um array, pega as chaves dos itens do array, senão pega as chaves do próprio objeto.
 */
type ResultKey<T extends object> = T extends Array<infer U> ? Array<keyof U>[number] : keyof T;

/**
 * Interface que representa um tag de provider utilizado para invalidar/atualizar cache em endpoints.
 * @template T - Tipo do tag (string literal)
 */
type TagProvider<T extends string> = { type: T; id?: string | number };

/**
 * Função utilitária para geração de tags para endpoints RTK Query.
 * Permite customizar como os tags são gerados a partir do resultado ou erro da requisição.
 *
 * @template TArg   - Tipo dos argumentos da query/mutation.
 * @template TMeta  - Tipo de meta.
 * @template TResult  - Tipo do resultado esperado (array ou objeto).
 * @template TError   - Tipo do erro, normalmente FetchBaseQueryError.
 * @template TTag     - Tipo literal do nome do tag para query de sucesso.
 * @template TTagErr  - Tipo literal do nome do tag para erro.
 *
 * @param config      - Objeto de configuração com as opções do tag:
 *   - tagType: string (tipo base do tag)
 *   - resultOptions: (opcional) como extrair id dos itens (key ou getKey customizado)
 *   - errorOptions:  (opcional) funções para prover tags via erro/status do erro
 *
 * @returns Uma função que gera os tags com base em resultado e erro.
 *
 * @example
 * provideTags({
 *   tagType: "User",
 *   resultOptions: { key: "id" }
 * })
 */
const provideTags =
  <
    TArg = unknown,
    TMeta = unknown,
    TResult extends Result[] | Result = Result[] | Result,
    TError extends FetchBaseQueryError = FetchBaseQueryError,
    TTag extends string = string,
    TTagErr extends string = string,
    TTagCustom extends string = string
  >({
    tagType,
    resultOptions,
    errorOptions,
    argOptions
  }: {
    tagType: TTag;
    resultOptions?: {
      key?: ResultKey<TResult>;
      getKey?: (item: TResult extends Array<infer U> ? U : TResult extends object ? TResult : never) => string | number;
    };
    errorOptions?: {
      tagByErr?: (error: TError) => TagProvider<TTagErr>[];
      tagByStatusErr?: Map<TError["status"], TagProvider<TTagErr>>;
    };
    argOptions?: {
      key?: ResultKey<TArg extends object ? TArg : never>;
      getKey?: (arg: TArg) => string | number;
      tagByArg?: (arg: TArg) => TagProvider<TTag>[];
    };
    customTags?: (result: TResult | undefined, error: TError | undefined, arg: TArg, _meta: TMeta) => TagProvider<TTagCustom>[];
  }) =>
  (result: TResult | undefined, error: TError | undefined, arg: TArg, _meta: TMeta) => {
    if (arg) {
      const getArgId = (arg: TArg): string | number | undefined => {
        if (argOptions?.getKey) return argOptions.getKey(arg);
        if (argOptions?.key) return arg[argOptions.key] as unknown as string | number | undefined;
        return (arg as { id: string | number }).id;
      };

      const argId = getArgId(arg);
      if (argId) {
        if (argOptions?.tagByArg) return argOptions.tagByArg(arg);
        return [{ type: tagType, id: argId }];
      }
      return [{ type: tagType, id: "LIST" }];
    }

    if (!result) {
      // Sem erro, retorna o tag padrão de lista
      if (!error) return [{ type: tagType, id: "LIST" }];

      // Se houver opções de erro configuradas
      if (errorOptions) {
        const { tagByErr, tagByStatusErr } = errorOptions;

        // Primeiramente, tenta por função customizada
        if (typeof tagByErr === "function") {
          const tags = tagByErr(error);
          if (tags?.length) return tags;
        }

        // Em segundo, tenta obter pelo status do erro
        if (tagByStatusErr instanceof Map && "status" in error) {
          const status = error.status;
          const tag = tagByStatusErr.get(status);
          if (tag) return [tag];
        } else if (tagByStatusErr instanceof Map) {
          // Se não conseguimos inferir o status, retorna todos os mapeamentos
          return Array.from(tagByStatusErr).map(([status, { type, id }]) => ({
            type,
            id: id ?? status
          }));
        }
      }

      // Por padrão, retorna o tag do tipo lista para cache invalidation de listas
      return [{ type: tagType, id: "LIST" }];
    }

    const getId = (item: TResult extends Array<infer U> ? U : TResult extends object ? TResult : never): string | number | undefined => {
      if (resultOptions?.getKey) return resultOptions.getKey(item);
      if (resultOptions?.key) return item[resultOptions.key];
      return item.id;
    };

    const list = Array.isArray(result) ? result : [result];

    return [{ type: tagType, id: "LIST" }, ...list.map((item) => ({ type: tagType, id: getId(item) }))];
  };

export { calculateDaysLeft, provideTags, sanitizeString };
